import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type * as API from "@shared-core/data/api-types";
import type { VatTreatment } from "@shared-core/data/catalogue-types";
import type {
  LiveCostingDetail,
  QuarantinedPrice,
} from "@shared-core/data/cost-engine-validation-types";
import { THARSTERN_BACK_END_CODE } from "@shared-core/data/fulfilment-route-types";
import {
  formatCurrencyGbp,
  formatDateTimeOrDash,
} from "@shared-core/domain/formatters";
import { summarizeNarrowing } from "@shared-core/domain/dictionary-used-by-presentation";
import { fulfilmentLabel } from "@shared-core/domain/catalogue-presentation";
import { paths } from "@shared-core/routing/routes";
import { SettingsBreadcrumbs } from "../app/components/patterns/SettingsBreadcrumbs";
import { Badge } from "../app/components/primitives/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../app/components/primitives/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../app/components/primitives/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../app/components/primitives/tabs";
import { ItemMappingsPanel } from "../app/components/domain/catalogue/ItemMappingsPanel";
import { LiveCostingPanel } from "../app/components/domain/catalogue/LiveCostingPanel";
import { ValidationCostMatrix } from "../app/components/domain/catalogue/ValidationCostMatrix";
import { ArrowUpRight, Loader2 } from "lucide-react";

/** Live costing, Definition and Used by are addressed by `?tab=`; Mappings
 *  by its own path (`…/:dictionaryItemId/mappings`), which the router
 *  passes in as `routeTab` so links from cost drill-downs stay stable. */
type QueryTab = "live-costing" | "definition" | "used-by";
type ItemTab = QueryTab | "mappings";
const QUERY_TABS: QueryTab[] = ["live-costing", "definition", "used-by"];

/** The breadcrumb trail shared by every state of this page — Settings ›
 *  Catalogue › Dictionary, plus this item's own name once it has loaded. */
function crumbs(
  leaf?: string,
  family?: { code: string; label: string } | null,
) {
  const base = [
    { label: "Settings", to: paths.settingsIntegrations() },
    { label: "Catalogue", to: paths.settingsCatalogues() },
  ];
  return leaf
    ? [
        ...base,
        { label: "Dictionary", to: paths.dictionaryAdmin() },
        // Family › product: the family crumb opens the dictionary on its family.
        ...(family
          ? [
              {
                label: family.label,
                to: paths.dictionaryAdmin({ family: family.code }),
              },
            ]
          : []),
        { label: leaf },
      ]
    : [...base, { label: "Dictionary" }];
}

const VAT_TREATMENT_LABELS: Record<VatTreatment, string> = {
  standard: "Standard rate (20%)",
  reduced: "Reduced rate (5%)",
  zero_rated: "Zero-rated (0%)",
};

/**
 * An option's `requires` rule (R28) as the admin reads it: "needs Pages: 6pp, 8pp". A rule allowing every
 * option of the field but its default reads "needs a Banded in": eight counts repeated on every banded
 * option said nothing the field did not.
 */
function describeRequirement(
  fields: API.DictionaryItemAdminDetail["fields"],
  fieldKey: string,
  codes: string[],
): string {
  const field = fields.find((f) => f.key === fieldKey);
  const label = field?.label ?? fieldKey;
  const allowed = new Set(codes);
  const excluded = field?.options.filter((o) => !allowed.has(o.code)) ?? [];
  if (
    field &&
    codes.length > 1 &&
    excluded.length === 1 &&
    excluded[0].is_default
  ) {
    return `needs a ${label}`;
  }
  const labels = codes.map(
    (code) => field?.options.find((o) => o.code === code)?.label ?? code,
  );
  return `needs ${label}: ${labels.join(", ")}`;
}

type AdminOption =
  API.DictionaryItemAdminDetail["fields"][number]["options"][number];

/**
 * A paper field's options as its types, each with its weights ("Silk: 250, 300, 350gsm"), as the item pages
 * offer them (R204); null when the field is not one, or a type would hold a single weight anyway.
 */
function paperTypes(
  options: AdminOption[],
): { label: string; options: AdminOption[] }[] | null {
  if (options.length < 2 || !options.every((o) => o.paper)) return null;
  const byType = new Map<string, { label: string; options: AdminOption[] }>();
  for (const option of options) {
    const paper = option.paper!;
    const type = byType.get(paper.type_code) ?? {
      label: paper.type_label,
      options: [],
    };
    type.options.push(option);
    byType.set(paper.type_code, type);
  }
  const types = [...byType.values()];
  if (types.length < 2 || !types.some((t) => t.options.length > 1)) return null;
  for (const type of types)
    type.options.sort((a, b) => a.paper!.weight - b.paper!.weight);
  return types;
}

function OptionChip({
  fields,
  option,
  label,
}: {
  fields: API.DictionaryItemAdminDetail["fields"];
  option: AdminOption;
  label: string;
}) {
  return (
    <li className="flex items-center gap-1.5 rounded-md border border-border px-2 py-1 text-xs">
      <span>{label}</span>
      {Object.entries(option.requires ?? {}).map(([fieldKey, codes]) => (
        <span key={fieldKey} className="text-muted-foreground">
          {describeRequirement(fields, fieldKey, codes)}
        </span>
      ))}
      {option.is_default && (
        <Badge variant="neutral" className="text-[10px]">
          default
        </Badge>
      )}
      {option.price_delta_pence !== 0 && (
        <span className="text-muted-foreground">
          {option.price_delta_pence > 0 ? "+" : ""}
          {formatCurrencyGbp(option.price_delta_pence / 100, 2)}
        </span>
      )}
    </li>
  );
}

function DefinitionSection({
  detail,
}: {
  detail: API.DictionaryItemAdminDetail;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Definition</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {detail.fields.map((field) => (
          <div key={field.id} className="space-y-1.5">
            <div className="flex items-center gap-2 text-sm font-medium">
              {field.section && (
                <span className="text-muted-foreground">{field.section} ·</span>
              )}
              {field.label}
              <span className="text-xs font-normal text-muted-foreground">
                {field.mode}
              </span>
            </div>
            {field.custom_hint && (
              <p className="text-xs text-muted-foreground">
                {field.custom_hint}
              </p>
            )}
            {field.mode === "fixed" ? (
              <p className="text-sm text-muted-foreground">
                {field.fixed_value ?? "—"}
              </p>
            ) : paperTypes(field.options) ? (
              <div className="space-y-1.5">
                {paperTypes(field.options)!.map((type) => (
                  <div
                    key={type.label}
                    className="flex flex-wrap items-center gap-2"
                  >
                    <span className="w-40 shrink-0 text-xs text-muted-foreground">
                      {type.label}
                    </span>
                    <ul className="flex flex-wrap gap-2">
                      {type.options.map((option) => (
                        <OptionChip
                          key={option.id}
                          fields={detail.fields}
                          option={option}
                          label={option.paper!.weight_label}
                        />
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ) : (
              <ul className="flex flex-wrap gap-2">
                {field.options.map((option) => (
                  <OptionChip
                    key={option.id}
                    fields={detail.fields}
                    option={option}
                    label={option.label}
                  />
                ))}
              </ul>
            )}
          </div>
        ))}

        <div className="border-t border-border pt-3">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-2">
            Quantity ladder
          </p>
          <ul className="flex flex-wrap gap-2">
            {detail.quantity_breaks.map((qb) => (
              <li
                key={qb.id}
                className="rounded-md border border-border px-2 py-1 text-xs tabular-nums"
              >
                {qb.qty.toLocaleString()}
              </li>
            ))}
          </ul>
        </div>

        {/* Default VAT treatment — what a catalogue item derived from this
            dictionary item starts with. Declared in the item's file (#68),
            so it is shown here, not edited. */}
        <div className="border-t border-border pt-3 space-y-1.5">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Default VAT treatment
          </p>
          <p className="text-sm">
            {detail.dictionary_item.default_vat_treatment
              ? VAT_TREATMENT_LABELS[
                  detail.dictionary_item.default_vat_treatment
                ]
              : "Not set"}
          </p>
          <p className="text-xs text-muted-foreground">
            Copied onto a catalogue item when derived; each item&apos;s own
            treatment can be changed in the catalogue editor.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

function UsedBySection({
  entries,
  loadError,
  onRetry,
}: {
  entries: API.UsedByEntry[] | null;
  loadError: boolean;
  onRetry: () => void;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Used by</CardTitle>
      </CardHeader>
      <CardContent>
        {loadError ? (
          <p className="text-sm text-destructive">
            Could not load where this item is used.{" "}
            <button
              type="button"
              onClick={onRetry}
              className="font-medium underline underline-offset-2"
            >
              Try again
            </button>
          </p>
        ) : entries === null ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> Loading…
          </div>
        ) : entries.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No catalogue item derives from this dictionary item yet.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Item</TableHead>
                <TableHead>Catalogue</TableHead>
                <TableHead>Depth</TableHead>
                <TableHead>Narrowing</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.map((entry) => (
                <TableRow key={entry.item_id}>
                  <TableCell>
                    <Link
                      to={paths.settingsCatalogueItemEdit({
                        catalogueId: entry.catalogue_id,
                        itemId: entry.item_id,
                      })}
                      className="inline-flex items-center gap-1 font-medium hover:underline"
                    >
                      {entry.item_name}
                      <ArrowUpRight className="size-3.5" />
                    </Link>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {entry.catalogue_name}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {entry.depth === 1
                      ? "direct"
                      : `via ${entry.depth - 1} hop${entry.depth - 1 === 1 ? "" : "s"}`}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {summarizeNarrowing(entry.narrowing)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

export function DictionaryItemAdminPage({
  routeTab,
}: {
  /** Set by the Mappings route; absent on the item's own path. */
  routeTab?: "mappings";
} = {}) {
  const adapter = useDataAdapter();
  const navigate = useNavigate();
  const { dictionaryItemId } = useParams<{ dictionaryItemId: string }>();

  const [detail, setDetail] = useState<API.DictionaryItemAdminDetail | null>(
    null,
  );
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [usedBy, setUsedBy] = useState<API.UsedByEntry[] | null>(null);
  const [usedByLoadError, setUsedByLoadError] = useState(false);
  const [liveCosting, setLiveCosting] = useState<LiveCostingDetail | null>(
    null,
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get("tab");
  const activeTab: ItemTab =
    routeTab ??
    (QUERY_TABS.includes(rawTab as QueryTab)
      ? (rawTab as QueryTab)
      : "live-costing");
  const setActiveTab = (tab: string) => {
    if (!dictionaryItemId) return;
    if (tab === "mappings") {
      navigate(paths.dictionaryItemMappings({ dictionaryItemId }));
      return;
    }
    if (routeTab) {
      // Leaving the Mappings path for a `?tab=` tab on the item's own path.
      const itemPath = paths.dictionaryItemAdmin({ dictionaryItemId });
      navigate(tab === "live-costing" ? itemPath : `${itemPath}?tab=${tab}`);
      return;
    }
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (tab === "live-costing") params.delete("tab");
        else params.set("tab", tab);
        return params;
      },
      { replace: true },
    );
  };

  const load = useCallback(() => {
    if (!dictionaryItemId) return;
    setLoadError(false);
    adapter
      .getDictionaryItemAdminDetail(dictionaryItemId)
      .then((res) => {
        if (res === null) {
          setNotFound(true);
          return;
        }
        setDetail(res);
      })
      // A rejection (network/server error) is not the same fact as the
      // adapter returning null (genuinely no such item) — conflating them
      // showed "not found" for a page that would load fine on retry
      // (coordinator review round 6).
      .catch(() => setLoadError(true));
  }, [adapter, dictionaryItemId]);

  const loadLiveCosting = useCallback(() => {
    if (!dictionaryItemId) return;
    adapter
      .getDictionaryLiveCostingDetail(dictionaryItemId)
      .then(setLiveCosting);
  }, [adapter, dictionaryItemId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadLiveCosting();
  }, [loadLiveCosting]);

  // A supplier-only item (decision #71) has no Tharstern product type —
  // its mapping has no Tharstern subtree — so it is not made in-house and
  // live costing does not apply. An item with no mapping file is made
  // in-house, as every item was before.
  const [isMadeInHouse, setIsMadeInHouse] = useState(true);
  useEffect(() => {
    if (!dictionaryItemId) return;
    adapter
      .getDictionaryItemMappings(dictionaryItemId)
      .then((mappings) =>
        setIsMadeInHouse(
          mappings === null ||
            THARSTERN_BACK_END_CODE in mappings.item.byBackEnd,
        ),
      )
      .catch(() => setIsMadeInHouse(true));
  }, [adapter, dictionaryItemId]);

  async function handleReleaseQuarantine(
    price: QuarantinedPrice,
    note: string,
  ) {
    if (!dictionaryItemId) return;
    const updated = await adapter.releaseQuarantinedPrice(
      dictionaryItemId,
      price.releaseUrl,
      note,
    );
    setLiveCosting(updated);
  }

  const loadUsedBy = useCallback(() => {
    if (!dictionaryItemId || notFound || loadError) return;
    setUsedByLoadError(false);
    adapter
      .getDictionaryUsedBy(dictionaryItemId)
      .then(setUsedBy)
      // A failed fetch is not the same fact as "nothing uses this item" —
      // conflating them answered "is anything using this?" with a lie.
      .catch(() => setUsedByLoadError(true));
  }, [adapter, dictionaryItemId, notFound, loadError]);

  useEffect(() => {
    loadUsedBy();
  }, [loadUsedBy]);

  if (notFound) {
    return (
      <div className="space-y-4">
        <SettingsBreadcrumbs crumbs={crumbs()} />
        <p className="text-muted-foreground">
          Dictionary item not found. The link may be out of date.
        </p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="space-y-4">
        <SettingsBreadcrumbs crumbs={crumbs()} />
        <p className="text-destructive">
          Couldn&rsquo;t load this dictionary item.{" "}
          <button
            type="button"
            onClick={load}
            className="font-medium underline underline-offset-2"
          >
            Try again
          </button>
        </p>
      </div>
    );
  }

  if (!detail || !dictionaryItemId) {
    return (
      <div className="space-y-4">
        <SettingsBreadcrumbs crumbs={crumbs()} />
        <p className="text-muted-foreground">Loading…</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <SettingsBreadcrumbs
        crumbs={crumbs(
          detail.dictionary_item.name,
          detail.dictionary_item.family && detail.dictionary_item.family_label
            ? {
                code: detail.dictionary_item.family,
                label: detail.dictionary_item.family_label,
              }
            : null,
        )}
      />

      <div>
        <h1 className="text-2xl font-semibold text-foreground">
          {detail.dictionary_item.name}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          {isMadeInHouse
            ? `PT ${detail.dictionary_item.product_type_id}`
            : "Not made in-house"}{" "}
          · {fulfilmentLabel(detail.dictionary_item.fulfilment)} · Definition
          last changed {formatDateTimeOrDash(detail.definition_last_changed_at)}
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="live-costing">Live costing</TabsTrigger>
          <TabsTrigger value="definition">Definition</TabsTrigger>
          <TabsTrigger value="used-by">Used by</TabsTrigger>
          <TabsTrigger value="mappings">Mappings</TabsTrigger>
        </TabsList>

        <TabsContent value="live-costing" className="space-y-6">
          {!isMadeInHouse ? (
            // Nothing is costed in-house.
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Live costing</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground">
                Not made in-house
              </CardContent>
            </Card>
          ) : liveCosting ? (
            <LiveCostingPanel
              detail={liveCosting}
              derivedItemCount={usedBy?.length ?? 0}
              onReleaseQuarantine={handleReleaseQuarantine}
            />
          ) : (
            <Card>
              <CardContent className="py-4 text-sm text-muted-foreground">
                <Loader2 className="mr-2 inline size-4 animate-spin" /> Loading
                live costing checks…
              </CardContent>
            </Card>
          )}
          <ValidationCostMatrix dictionaryItemId={dictionaryItemId} />
        </TabsContent>

        <TabsContent value="definition" className="space-y-6">
          <DefinitionSection detail={detail} />
        </TabsContent>

        <TabsContent value="used-by">
          <UsedBySection
            entries={usedBy}
            loadError={usedByLoadError}
            onRetry={loadUsedBy}
          />
        </TabsContent>

        <TabsContent value="mappings">
          <ItemMappingsPanel dictionaryItemId={dictionaryItemId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
